import { useIntl } from 'react-intl';
import { ServiceStatisticsPriorityDTO } from '../../dto/ServiceStatisticsDTO';
import { formatInt, formatMinutes } from './statisticsUtils';

interface PriorityListProps {
    priorities: ServiceStatisticsPriorityDTO[];
    /** Código aplicado como filtro; 0 si no hay. */
    selectedId: number;
    /** Elegir un código lo aplica como filtro; elegir el aplicado lo quita. */
    onToggle: (priorityId: number) => void;
}

/**
 * Atenciones y espera media por código de atención. Cada fila es un botón que filtra la pantalla por ese código; la
 * duración media va en el tooltip, para no repetir los números de la fila.
 * TODO(design-system): gráficos. Novarium no define las barras horizontales; la barra es un adorno de la cantidad, que
 * está escrita al lado.
 */
export default function PriorityList({ priorities, selectedId, onToggle }: PriorityListProps) {
    const intl = useIntl();
    const items = priorities.filter(item => item.attentions > 0).sort((a, b) => b.attentions - a.attentions);
    if (items.length === 0) {
        return <p className="nv-muted ss-empty">{intl.formatMessage({ id: 'ServicesStatisticsNoResults' })}</p>;
    }
    const max = Math.max(...items.map(item => item.attentions));

    return (
        <ul className="ss-priorities">
            {items.map(item => {
                const selected = item.priorityId === selectedId;
                const duration = intl.formatMessage({ id: 'ServicesStatisticsPriorityDuration' },
                    { value: formatMinutes(item.avgServiceMinutes) });
                const summary = intl.formatMessage({ id: 'ServicesStatisticsPrioritySummary' },
                    { attentions: formatInt(item.attentions), wait: formatMinutes(item.avgWaitMinutes) });
                return (
                    <li key={item.priorityId}>
                        <button
                            type="button"
                            className={`ss-priority nv-tip${selected ? ' ss-priority--selected' : ''}`}
                            aria-pressed={selected}
                            aria-label={`${item.name}. ${summary}. ${duration}`}
                            data-pr-tooltip={duration}
                            onClick={() => onToggle(item.priorityId)}
                        >
                            <span className="ss-priority__name">{item.name}</span>
                            <span className="ss-priority__track" aria-hidden="true">
                                <span className="ss-priority__bar" style={{ width: `${(item.attentions / max) * 100}%` }} />
                            </span>
                            <span className="ss-priority__value">{summary}</span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
